import { AsyncPipe } from '@angular/common';
import { Component, input, signal } from '@angular/core';

import { MatDialog } from '@angular/material/dialog';

import {
  Association,
  AssociationCardData,
  BrandingService,
  CardAction,
  CommonAssociationCardContentComponent,
  CrmFieldService,
  CrmObjectAssociationPanelComponent,
  formatAsReadonlyPhone,
  ObjectType,
  PAGE_ROUTES,
  parsePhoneNumber,
  StandardIds,
} from '@galaxy/crm/static';
import {
  AccessBusinessAppComponent,
  AccessBusinessAppService,
} from '../association-cards/access-business-app/access-business-app.component';
import { AppAccess } from '../association-cards/access-business-app/access-business-app.service';
import { take } from 'rxjs';
import { MatCardSubtitle } from '@angular/material/card';

export interface CompanyAssociationCardData extends AssociationCardData {
  phone?: string;
  website?: string;
  address?: string;
  businessAppAccess?: AppAccess;
}

@Component({
  selector: 'app-pcc-crm-contact-to-company-associations-card',
  templateUrl: './pcc-contact-to-company-associations-card.component.html',
  styleUrls: ['./pcc-contact-to-company-associations-card.component.scss'],
  imports: [
    CrmObjectAssociationPanelComponent,
    CommonAssociationCardContentComponent,
    AccessBusinessAppComponent,
    MatCardSubtitle,
    AsyncPipe,
  ],
  providers: [BrandingService, AccessBusinessAppService],
})
export class PccContactToCompanyAssociationsCardComponent {
  id = input<string>('');
  name = input<string>('');
  objectType = input<ObjectType>('Contact');
  customActions = signal<CardAction[]>([]);
  cardFunction = (associations: Association[], routePrefix: string): CompanyAssociationCardData[] => {
    return associations.map((association) => {
      const phoneParsed = parsePhoneNumber(
        this.fieldService.getFieldValueFromCrmObject(association.associatedObject, StandardIds.CompanyPhoneNumber)
          ?.stringValue || '',
      );
      const name =
        this.fieldService.getFieldValueFromCrmObject(association.associatedObject, StandardIds.CompanyName)
          ?.stringValue || '';
      const businessAppAccess = this.accessBusinessAppService.getAppAccess(
        this.dialog,
        this.id() || '',
        association.associatedObject.crmObjectId || '',
        this.name(),
        name,
      );

      businessAppAccess?.actions$?.pipe(take(1)).subscribe((actions) => {
        this.customActions.set(actions);
      });

      return {
        associationId: association.associationId,
        objectId: association.associatedObject.crmObjectId || '',
        name: name,
        profileUrl: `${routePrefix}/${PAGE_ROUTES.COMPANY.ROOT}/${PAGE_ROUTES.COMPANY.SUBROUTES.PROFILE}`.replace(
          ':crmObjectId',
          association.associatedObject.crmObjectId || '',
        ),
        tags: association.tags || [],
        icon: 'business',

        address: this.generateAddressString(association),
        website: this.fieldService.getFieldValueFromCrmObject(association.associatedObject, StandardIds.CompanyWebsite)
          ?.stringValue,
        phone: formatAsReadonlyPhone(phoneParsed),
        businessAppAccess: businessAppAccess,
      } as CompanyAssociationCardData;
    });
  };

  constructor(
    private readonly fieldService: CrmFieldService,
    private readonly accessBusinessAppService: AccessBusinessAppService,
    private readonly dialog: MatDialog,
  ) {}

  generateAddressString(association: Association): string {
    const address1 = this.fieldService.getFieldValueFromCrmObject(
      association.associatedObject,
      StandardIds.CompanyPrimaryAddressLine1,
    )?.stringValue;

    const address2 = this.fieldService.getFieldValueFromCrmObject(
      association.associatedObject,
      StandardIds.CompanyPrimaryAddressLine2,
    )?.stringValue;

    const city = this.fieldService.getFieldValueFromCrmObject(
      association.associatedObject,
      StandardIds.CompanyPrimaryAddressCity,
    )?.stringValue;

    const state = this.fieldService.getFieldValueFromCrmObject(
      association.associatedObject,
      StandardIds.CompanyPrimaryAddressState,
    )?.stringValue;

    const country = this.fieldService.getFieldValueFromCrmObject(
      association.associatedObject,
      StandardIds.CompanyPrimaryAddressCountry,
    )?.stringValue;

    const zip = this.fieldService.getFieldValueFromCrmObject(
      association.associatedObject,
      StandardIds.CompanyPrimaryAddressPostalCode,
    )?.stringValue;

    return [address1, address2, city, state, country, zip].filter(Boolean).join(', ');
  }
}
